import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { type ActionContext, type ActionState, type ToolbarPreferences } from '../types';

export interface ToolbarState {
  // Visibility
  isVisible: boolean;
  scrollControlEnabled: boolean;

  // Actions configuration
  actions: Record<string, ActionState>;

  // Context for actions
  actionContext?: ActionContext;

  // UI preferences
  preferences: ToolbarPreferences;

  // Scroll container selector
  scrollContainerSelector?: string;
}

const initialState: ToolbarState = {
  isVisible: false, // Default to hidden, explicitly show in specific views
  scrollControlEnabled: true,
  actions: {},
  actionContext: undefined,
  preferences: {
    position: 'bottom',
    compactMode: false,
  },
};

export const toolbarSlice = createSlice({
  name: 'toolbar',
  initialState,
  reducers: {
    showToolbar: state => {
      state.isVisible = true;
    },

    hideToolbar: state => {
      state.isVisible = false;
    },

    toggleToolbar: state => {
      state.isVisible = !state.isVisible;
    },

    setScrollControlEnabled: (state, action: PayloadAction<boolean>) => {
      state.scrollControlEnabled = action.payload;
    },

    setActionContext: (state, action: PayloadAction<ActionContext | undefined>) => {
      state.actionContext = action.payload;
    },

    updateActionContext: (state, action: PayloadAction<Partial<ActionContext>>) => {
      state.actionContext = {
        ...state.actionContext,
        ...action.payload,
      };
    },

    initializeAction: (state, action: PayloadAction<{ id: string; enabled?: boolean }>) => {
      const { id, enabled = true } = action.payload;
      if (!state.actions[id]) {
        state.actions[id] = {
          id,
          enabled,
          badgeCount: 0,
          metadata: undefined,
        };
      }
    },

    setActionEnabled: (state, action: PayloadAction<{ actionId: string; enabled: boolean }>) => {
      const { actionId, enabled } = action.payload;
      if (state.actions[actionId]) {
        state.actions[actionId].enabled = enabled;
      }
    },

    updateBadgeCount: (state, action: PayloadAction<{ actionId: string; count: number }>) => {
      const { actionId, count } = action.payload;
      if (!state.actions[actionId]) {
        state.actions[actionId] = {
          id: actionId,
          enabled: true,
          badgeCount: count,
          metadata: undefined,
        };
      } else {
        state.actions[actionId].badgeCount = count;
      }
    },

    incrementBadgeCount: (state, action: PayloadAction<string>) => {
      const actionId = action.payload;
      if (!state.actions[actionId]) {
        state.actions[actionId] = {
          id: actionId,
          enabled: true,
          badgeCount: 1,
          metadata: undefined,
        };
      } else {
        state.actions[actionId].badgeCount += 1;
      }
    },

    decrementBadgeCount: (state, action: PayloadAction<string>) => {
      const actionId = action.payload;
      if (state.actions[actionId] && state.actions[actionId].badgeCount > 0) {
        state.actions[actionId].badgeCount -= 1;
      }
    },

    clearBadgeCount: (state, action: PayloadAction<string>) => {
      const actionId = action.payload;
      if (state.actions[actionId]) {
        state.actions[actionId].badgeCount = 0;
      }
    },

    setActionMetadata: (state, action: PayloadAction<{ actionId: string; metadata: unknown }>) => {
      const { actionId, metadata } = action.payload;
      if (state.actions[actionId]) {
        state.actions[actionId].metadata = metadata;
      }
    },

    setPreferences: (state, action: PayloadAction<Partial<ToolbarPreferences>>) => {
      state.preferences = {
        ...state.preferences,
        ...action.payload,
      };
    },

    setScrollContainerSelector: (state, action: PayloadAction<string | undefined>) => {
      state.scrollContainerSelector = action.payload;
    },

    resetState: () => initialState,
  },
});

export const {
  showToolbar,
  hideToolbar,
  toggleToolbar,
  setScrollControlEnabled,
  setActionContext,
  updateActionContext,
  initializeAction,
  setActionEnabled,
  updateBadgeCount,
  incrementBadgeCount,
  decrementBadgeCount,
  clearBadgeCount,
  setActionMetadata,
  setPreferences,
  setScrollContainerSelector,
  resetState,
} = toolbarSlice.actions;

export default toolbarSlice.reducer;
